import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ObservationRecord } from '../../types/observations';
import {
  CORRELATION_DESCRIPTIONS,
  CORRELATION_LABELS,
  CorrelationCardRecord,
  CorrelationCardStatus,
  CorrelationId,
  CorrelationsDocRecord,
  DEFAULT_CORRELATION_OPTINS,
  THREE_WEEK_THRESHOLD,
} from '../../types/correlations';
import {
  computeAndSaveCorrelations,
  getCorrelations,
} from '../../services/correlationService';
import {
  getTrackerSettings,
  getTrackerSummary,
} from '../../services/dailyBiteService';

/* ────────────────────────────────────────────────────────────────────────────
   Daily Bite correlations — member-facing surface for Phase 3 observations.

   Renders one card per opted-in correlation. Before the 3-week threshold
   each card is a "still gathering" placeholder so the member knows the
   feature exists without us pretending to have signal we don't.

   This section is intentionally editorial, not interactive — the goal is
   observation, not prescription. Copy lives on the card records (written
   by the service) rather than here; this file is just layout.

   Privacy boundary: correlation output is never shared with clinicians
   or partners. If/when that changes it requires an explicit opt-in flow,
   not a rules tweak. See firestore.rules → /correlations/{docId}.
   ──────────────────────────────────────────────────────────────────────── */

interface DailyBiteCorrelationsSectionProps {
  uid: string;
  wearableObservations: ObservationRecord[];
}

export default function DailyBiteCorrelationsSection({
  uid,
  wearableObservations,
}: DailyBiteCorrelationsSectionProps) {
  const [doc, setDoc] = useState<CorrelationsDocRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [optIns, setOptIns] = useState(DEFAULT_CORRELATION_OPTINS);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [settings, summary, cached] = await Promise.all([
          getTrackerSettings(uid),
          getTrackerSummary(uid),
          getCorrelations(uid),
        ]);
        if (cancelled) return;
        const nextOptIns = {
          ...DEFAULT_CORRELATION_OPTINS,
          ...(settings.correlations || {}),
        };
        setOptIns(nextOptIns);
        // Show the cached doc immediately so the member doesn't stare at a
        // spinner, then recompute with the wearable observations this page
        // has loaded (which are the wearable-backed cards' only chance at
        // real signal).
        setDoc(cached);
        setLoading(false);
        try {
          const fresh = await computeAndSaveCorrelations({
            uid,
            settings,
            summary,
            wearableObservations,
          });
          if (!cancelled) setDoc(fresh);
        } catch (err) {
          console.error('correlations recompute (intelligence) failed', err);
        }
      } catch (err) {
        console.error('load correlations failed', err);
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // Deliberately not depending on wearableObservations identity — the
    // parent page loads them once. Re-runs on uid change are what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const visibleCards = useMemo(() => {
    if (!doc) return [];
    return doc.cards.filter((card) => {
      if (card.status === 'opted_out') return false;
      return optIns[card.id];
    });
  }, [doc, optIns]);

  if (loading) return null;
  if (!doc || visibleCards.length === 0) return null;

  const belowThreshold = doc.weeksCovered < THREE_WEEK_THRESHOLD;

  return (
    <section
      className="rounded-3xl border p-6 md:p-8 bg-white"
      style={{ borderColor: 'var(--melio-border)' }}
    >
      <div className="mb-5 flex items-start justify-between gap-4 flex-col md:flex-row md:items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-sage)' }}>
            Daily Bite · patterns
          </p>
          <h2
            className="text-2xl font-normal"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            {belowThreshold
              ? 'Watching a few patterns as they form'
              : 'A few patterns we\u2019re watching in your data'}
          </h2>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: 'var(--melio-text-muted)' }}>
            These are observations, not claims. We look for things that move together in your own data &mdash; never comparisons to other people, never causal language. Private to you.{' '}
            <Link
              to="/today/settings"
              className="underline"
              style={{ color: 'var(--melio-olive-deep)' }}
            >
              Change what gets watched
            </Link>
            .
          </p>
        </div>
        <span
          className="px-3 py-1.5 rounded-full text-xs uppercase tracking-wide shrink-0"
          style={{
            backgroundColor: belowThreshold
              ? 'rgba(196, 164, 132, 0.18)'
              : 'rgba(79, 93, 24, 0.14)',
            color: 'var(--melio-charcoal)',
          }}
        >
          {belowThreshold
            ? `${doc.weeksCovered} of ${THREE_WEEK_THRESHOLD} weeks`
            : `${doc.daysCovered} days in ${doc.windowDays}`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleCards.map((card) => (
          <CorrelationTile key={card.id} card={card} />
        ))}
      </div>
    </section>
  );
}

function statusPalette(status: CorrelationCardStatus): { bg: string; accent: string } {
  switch (status) {
    case 'observed':
      return { bg: 'rgba(79, 93, 24, 0.08)', accent: 'var(--melio-olive-deep)' };
    case 'gathering':
      return { bg: 'rgba(196, 164, 132, 0.14)', accent: 'var(--melio-terracotta-deep)' };
    case 'insufficient_signal':
      return { bg: 'rgba(31, 42, 30, 0.04)', accent: 'var(--melio-text-muted)' };
    default:
      return { bg: 'rgba(31, 42, 30, 0.04)', accent: 'var(--melio-text-muted)' };
  }
}

function statusLabel(status: CorrelationCardStatus): string {
  switch (status) {
    case 'observed':
      return 'Observed';
    case 'gathering':
      return 'Still gathering';
    case 'insufficient_signal':
      return 'Not enough yet';
    default:
      return '';
  }
}

function CorrelationTile({ card }: { card: CorrelationCardRecord }) {
  const palette = statusPalette(card.status);
  const label = statusLabel(card.status);
  const id = card.id as CorrelationId;

  return (
    <article
      className="rounded-2xl p-5"
      style={{ backgroundColor: palette.bg }}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p
          className="text-xs uppercase tracking-[0.18em]"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          {CORRELATION_LABELS[id]}
        </p>
        {label ? (
          <span
            className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: 'white',
              color: palette.accent,
              border: `1px solid ${palette.accent}`,
            }}
          >
            {label}
          </span>
        ) : null}
      </div>
      <h3
        className="text-lg font-normal mb-2 leading-snug"
        style={{
          fontFamily: "var(--melio-display)",
          color: 'var(--melio-charcoal)',
        }}
      >
        {card.headline}
      </h3>
      <p
        className="text-sm leading-relaxed"
        style={{ color: 'var(--melio-charcoal)' }}
      >
        {card.body}
      </p>
      {card.footnote ? (
        <p className="text-xs mt-2" style={{ color: 'var(--melio-text-muted)' }}>
          {card.footnote}
        </p>
      ) : null}
      <p
        className="text-[11px] mt-3 pt-3 border-t"
        style={{
          color: 'var(--melio-text-muted)',
          borderColor: 'rgba(31, 42, 30, 0.08)',
        }}
      >
        {CORRELATION_DESCRIPTIONS[id]}
      </p>
    </article>
  );
}
